import { useState } from "react";
import { useNavigate } from "react-router";
import { Check, X, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboarding, useCompleteOnboarding } from "@/api/hooks/use-onboarding";

/**
 * First-run guided setup for a tenant admin: a floating two-step card that
 * walks them from a fresh account to ready-to-send-traffic.
 *
 *   1/2  Add a provider API key   (auto-checks when a key is on file)
 *   2/2  Create a gateway key     (confirmed when they finish)
 *
 * Completion is persisted server-side (onboarding_completed_at), which also
 * flips the tenant to "Active" in the admin pane. Only shown to admins who
 * haven't finished — everyone else renders nothing.
 */
export function OnboardingGuide() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data, isLoading } = useOnboarding(Boolean(isAdmin));
  const complete = useCompleteOnboarding();
  const navigate = useNavigate();
  const [minimized, setMinimized] = useState(false);

  // Customers that signed up on their own get the OnboardingWizard instead
  // (ticket 17, ADR-0004).
  if (!isAdmin || isLoading || !data || data.completed || data.self_signup) return null;

  const step1Done = data.has_provider_key;
  // Step 2 is the explicit "I've created my gateway key" finish action — every
  // tenant starts with one auto-provisioned key, so we can't infer it from a
  // count. step1 must be done first.
  const currentStep = step1Done ? 2 : 1;

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full border border-electric-blue/40 bg-surface px-4 py-2 text-sm font-medium text-electric-blue shadow-lg transition-colors hover:bg-electric-blue/10"
      >
        <Sparkles size={15} />
        Finish setup ({step1Done ? "1" : "0"}/2)
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-40 w-[22rem] rounded-xl border border-line-subtle bg-surface p-5 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-fg">
            <Sparkles size={15} className="text-electric-blue" />
            Get set up
          </h3>
          <p className="mt-1 text-xs text-fg-muted">
            Two quick steps and you're ready to send traffic.
          </p>
        </div>
        <button
          onClick={() => setMinimized(true)}
          aria-label="Minimize"
          className="rounded-md p-1 text-fg-faint transition-colors hover:text-fg"
        >
          <X size={15} />
        </button>
      </div>

      <ol className="mt-4 space-y-3">
        <Step
          index={1}
          active={currentStep === 1}
          done={step1Done}
          title="Add your provider API key"
          body="Connect your OpenAI or Anthropic key so Phantm can route your traffic."
          actionLabel="Add API key"
          onAction={() => navigate("/settings?tab=provider-keys")}
        />
        <Step
          index={2}
          active={currentStep === 2}
          done={false}
          title="Create your gateway key"
          body="Generate the key your app will use to call Phantm. You'll see it once — save it somewhere safe."
          actionLabel="Create gateway key"
          onAction={() => navigate("/settings?tab=gateway-keys")}
          disabled={!step1Done}
        />
      </ol>

      <button
        onClick={() => complete.mutate()}
        disabled={!step1Done || complete.isPending}
        className="mt-4 w-full rounded-md bg-electric-blue px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-electric-blue/90 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {complete.isPending ? "Finishing…" : "I'm done — finish setup"}
      </button>
      {!step1Done && (
        <p className="mt-2 text-center text-[11px] text-fg-faint">
          Add a provider key first to finish.
        </p>
      )}
    </div>
  );
}

function Step({
  index,
  active,
  done,
  title,
  body,
  actionLabel,
  onAction,
  disabled,
}: {
  index: number;
  active: boolean;
  done: boolean;
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
  disabled?: boolean;
}) {
  return (
    <li className="flex gap-3">
      <span
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${
          done
            ? "border-safe-green/40 bg-safe-green/10 text-safe-green"
            : active
              ? "border-electric-blue/40 bg-electric-blue/10 text-electric-blue"
              : "border-line text-fg-faint"
        }`}
      >
        {done ? <Check size={13} /> : index}
      </span>
      <div className="flex-1">
        <p className={`text-sm font-medium ${done ? "text-fg-muted line-through" : "text-fg"}`}>
          {title}
        </p>
        {!done && <p className="mt-0.5 text-xs text-fg-muted">{body}</p>}
        {!done && (
          <button
            onClick={onAction}
            disabled={disabled}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-electric-blue transition-colors hover:text-electric-blue/80 disabled:cursor-not-allowed disabled:text-fg-faint"
          >
            {actionLabel}
            <ArrowRight size={13} />
          </button>
        )}
      </div>
    </li>
  );
}
